import { useState, useMemo, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AccountCircle } from "@mui/icons-material";
import { DataService } from "../../services/DataService";
import { OrganisationService } from "../../services/OrganisationService";
import { useOrganisations } from "../../hooks/queries/useOrganisations";

/**
 * Custom hook for NavBar state management
 */
export default function useNavBar(handleCollapsed, handleThemeChange) {
  const { logout, user } = useAuth0();
  const navigate = useNavigate();

  // TanStack Query hooks
  const { data: organisations = [] } = useOrganisations();

  // Redux selectors
  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );
  const themeMode = useSelector((state) => state.themeMode);
  const isMobile = useSelector((state) => state.isMobile);

  // Local state
  const [rotateChevron, setRotateChevron] = useState(
    localStorage.getItem("sidebarCollapsed") === "true" ? true : false
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElSidebar, setAnchorElSidebar] = useState(null);

  // Derive user image from user object
  const userImage = useMemo(() => {
    if (user && user.picture) {
      return <img src={user.picture} className="user-image" alt={user.name} />;
    }
    return <AccountCircle sx={{ color: "white" }} />;
  }, [user]);

  // Compute rotation style
  const rotate = rotateChevron ? "rotate(-180deg)" : "rotate(0)";

  // Handle sidebar collapse
  const handleCollapsedMenu = useCallback(() => {
    if (localStorage.getItem("sidebarCollapsed") === "true") {
      localStorage.setItem("sidebarCollapsed", "false");
    } else {
      localStorage.setItem("sidebarCollapsed", "true");
    }
    setRotateChevron(!rotateChevron);
    handleCollapsed();
  }, [rotateChevron, handleCollapsed]);

  // Handle organisation change
  const handleOrganisationChange = useCallback(
    (newValue) => {
      if (newValue) {
        OrganisationService.setSelectedOrganisation(newValue);
        localStorage.setItem("selectedOrganisationID", newValue.id);
        DataService.refreshData(user.email, user.sub);

        // If pageurl contains device, redirect to root
        if (window.location.href.includes("device")) {
          navigate("/");
        }
      }
    },
    [user, navigate]
  );

  // Handle menu open
  const handleMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  // Handle menu close
  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Handle sidebar menu close
  const handleCloseSidebar = useCallback(() => {
    setAnchorElSidebar(null);
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    localStorage.removeItem("selectedOrganisationID");
    logout({
      returnTo: window.location.origin,
    });
  }, [logout]);

  return {
    // State
    user,
    themeMode,
    isMobile,
    selectedOrganisation,
    organisations,
    rotateChevron,
    rotate,
    anchorEl,
    anchorElSidebar,
    userImage,

    // Actions
    handleThemeChange,
    handleMenu,
    handleClose,
    handleCloseSidebar,
    handleCollapsedMenu,
    handleOrganisationChange,
    handleLogout,
    setAnchorElSidebar,

    // Navigation
    navigate,
  };
}
